import React, { useState } from 'react';
import { observer } from 'mobx-react';
import Paragraph from 'antd/es/typography/Paragraph';
import styled from 'styled-components';
import { Tooltip, theme, GlobalToken } from 'antd';
import { Trans, useTranslation } from 'react-i18next';

import Super from '@/comp/Super';
import { useMeasurement } from '@/hooks/useMeasurement';

interface CustomGaugeProps {
  title: string;
  subtitle?: string;
  value: string | number;
  unit: string;
  upperThreshold?: number;
  lowerThreshold?: number;
}

const { useToken } = theme;

const CustomGauge = observer(({ title, subtitle, value, unit, upperThreshold, lowerThreshold }: CustomGaugeProps) => {
  const { t } = useTranslation();
  const { tvalue, tunit } = useMeasurement();
  const [shouldShowTooltip, setShouldShowTooltip] = useState(false);
  const { token } = useToken();

  const getBackgroundColor = (): string => {
    if (typeof value !== 'number') {
      return '';
    }

    if (upperThreshold != null && lowerThreshold != null) {
      if (value >= lowerThreshold && value <= upperThreshold) {
        return 'green';
      } else {
        return 'red';
      }
    } else if (upperThreshold != null) {
      if (value < upperThreshold) {
        return 'green';
      } else {
        return 'red';
      }
    } else if (lowerThreshold != null) {
      if (value >= lowerThreshold) {
        return 'green';
      } else {
        return 'red';
      }
    }
    return '';
  };
  const content = (
    <Container className={getBackgroundColor()}>
      <Paragraph ellipsis={{ onEllipsis: setShouldShowTooltip }} className="title ellipsis">
        {title}
      </Paragraph>
      {subtitle ? <div className="subtitle ellipsis">{subtitle}</div> : null}
      <div className="value-container">
        <div className="value">{tvalue(value, unit)}</div>
        <div className="unit">
          <Trans i18nKey={tunit(unit?.replace('^', ''))}>
            <Super />
          </Trans>
        </div>
      </div>
    </Container>
  );

  const tooltip =
    upperThreshold != null || lowerThreshold != null || shouldShowTooltip ? (
      <ul>
        {title}
        {lowerThreshold != null && (
          <li>
            Lower threshold: <span className="bold">{tvalue(lowerThreshold)}</span> {t(tunit(unit))}
          </li>
        )}
        {upperThreshold != null && (
          <li>
            Upper threshold: <span className="bold">{tvalue(upperThreshold)}</span> {t(tunit(unit))}
          </li>
        )}
      </ul>
    ) : null;

  return upperThreshold != null || lowerThreshold != null || shouldShowTooltip ? (
    <TooltipContainer title={tooltip} color={token.colorText} overlayInnerStyle={{ color: token.colorBgContainer }}>
      {content}
    </TooltipContainer>
  ) : (
    <>{content}</>
  );
});

const TooltipContainer = styled(Tooltip)`
  .bold {
    font-weight: ${({ theme: { token } }) => token.fontWeightStrong};
  }
`;

const Container = styled.div<{ theme: { token: GlobalToken } }>`
  height: 68px;
  border: 1px solid ${({ theme: { token } }) => token.colorBorder};
  padding: ${({ theme: { token } }) => token.paddingXXS}px;

  &.red {
    background-color: ${({ theme: { token } }) => token.colorErrorBorderHover};
    border-color: ${({ theme: { token } }) => token.colorErrorBorderHover};
  }

  &.green {
    border-color: ${({ theme: { token } }) => token.colorSuccessBorderHover};
    background-color: ${({ theme: { token } }) => token.colorSuccessBorderHover};
  }

  .title {
    color: ${({ theme: { token } }) => token.colorTextTertiary};
    text-transform: uppercase;
    font-weight: ${({ theme: { token } }) => token.fontWeightStrong};
    font-size: ${({ theme: { token } }) => token.fontSizeSM}px;
    margin: ${({ theme: { token } }) => token.marginXXS}px 0 0 0;
  }

  .subtitle {
    color: ${({ theme: { token } }) => token.colorBorder};
    text-transform: uppercase;
    font-size: ${({ theme: { token } }) => token.fontSizeSM}px;
    line-height: 10px;
  }

  .value-container {
    display: flex;
    align-items: baseline;
    justify-content: center;

    .value {
      font-size: ${({ theme: { token } }) => token.fontSizeXL}px;
      font-weight: ${({ theme: { token } }) => token.fontWeightStrong};
      color: ${({ theme: { token } }) => token.colorText};
      text-align: center;
    }

    .unit {
      color: ${({ theme: { token } }) => token.colorTextSecondary};
      font-size: ${({ theme: { token } }) => token.fontSizeSM}px;
      margin-left: ${({ theme: { token } }) => token.marginXXS}px;
    }
  }

  .ellipsis {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
`;

export default CustomGauge;
